/**
 * "View source" (ux-s11-transformations.md N9, ledger H14 and T7): the address of one file of the
 * customer's dbt repository on its own git host, at a ref or a commit. Pure and conservative: the
 * four hosts whose URL shapes are known (github.com, gitlab.com, dev.azure.com, bitbucket.org),
 * and null for anything else, so a screen shows no link rather than a guessed one.
 *
 * Never a credential in the answer: a `user:token@` part of the stored address is dropped, and
 * only https (or a `git@host:path` address, rewritten to https) is accepted.
 */

type Host = "github" | "gitlab" | "azure" | "bitbucket";

const HOSTS: Record<string, Host> = {
  "github.com": "github",
  "gitlab.com": "gitlab",
  "dev.azure.com": "azure",
  "bitbucket.org": "bitbucket",
};

/** A ref or commit this function puts in a URL: git's safe subset, no `..`, no leading `-`. */
const REF_RE = /^[A-Za-z0-9._/-]{1,200}$/;
/** A project-relative path segment: the dbt path rule's characters. */
const SEGMENT_RE = /^[A-Za-z0-9_.-]+$/;
const COMMIT_RE = /^[0-9a-f]{40}$/;

function parseRepo(repoUrl: string): { host: Host; parts: string[] } | null {
  let text = repoUrl.trim();
  const scp = /^[A-Za-z0-9._-]+@([A-Za-z0-9.-]+):(.+)$/.exec(text);
  if (scp) text = `https://${scp[1]}/${scp[2]}`;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  const host = HOSTS[url.hostname.toLowerCase()];
  if (!host) return null;
  let parts: string[];
  try {
    parts = url.pathname
      .replace(/\.git$/, "")
      .split("/")
      .filter((part) => part.length > 0)
      .map((part) => decodeURIComponent(part));
  } catch {
    return null;
  }
  if (parts.length < 2 || parts.some((part) => !SEGMENT_RE.test(part))) return null;
  return { host, parts };
}

/** `subdir/path`, each segment checked; null when either is not a plain relative path. */
function filePath(subdir: string | null, path: string): string[] | null {
  const segments = [...(subdir ?? "").split("/"), ...path.split("/")].filter(
    (segment) => segment.length > 0,
  );
  if (segments.length === 0) return null;
  if (
    segments.some((segment) => segment === ".." || segment === "." || !SEGMENT_RE.test(segment))
  ) {
    return null;
  }
  return segments;
}

function encodePath(segments: readonly string[]): string {
  return segments.map((segment) => encodeURIComponent(segment)).join("/");
}

export function repoFileUrl(
  repoUrl: string | null,
  refOrCommit: string | null,
  subdir: string | null,
  path: string | null,
): string | null {
  if (!repoUrl || !refOrCommit || !path) return null;
  if (!REF_RE.test(refOrCommit) || refOrCommit.includes("..") || refOrCommit.startsWith("-")) {
    return null;
  }
  const repo = parseRepo(repoUrl);
  const file = filePath(subdir, path);
  if (!repo || !file) return null;
  const ref = refOrCommit.split("/").map(encodeURIComponent).join("/");
  const fileText = encodePath(file);
  switch (repo.host) {
    case "github":
      if (repo.parts.length !== 2) return null;
      return `https://github.com/${encodePath(repo.parts)}/blob/${ref}/${fileText}`;
    case "gitlab":
      return `https://gitlab.com/${encodePath(repo.parts)}/-/blob/${ref}/${fileText}`;
    case "bitbucket":
      if (repo.parts.length !== 2) return null;
      return `https://bitbucket.org/${encodePath(repo.parts)}/src/${ref}/${fileText}`;
    case "azure": {
      // https://dev.azure.com/{org}/{project}/_git/{repo}
      const [org, project, git, name] = repo.parts;
      if (repo.parts.length !== 4 || git !== "_git" || !org || !project || !name) return null;
      const version = COMMIT_RE.test(refOrCommit) ? `GC${refOrCommit}` : `GB${refOrCommit}`;
      const filePath = `/${file.join("/")}`;
      return (
        `https://dev.azure.com/${encodePath([org, project])}/_git/${encodeURIComponent(name)}` +
        `?path=${encodeURIComponent(filePath)}&version=${encodeURIComponent(version)}`
      );
    }
  }
}

/**
 * Round 6 (T2): the repository's short name for the Details pane, "owner/repo" (GitLab's nested
 * groups and Azure DevOps' org and project kept, `_git` dropped); null for an unknown host.
 */
export function repoShortName(repoUrl: string | null): string | null {
  if (!repoUrl) return null;
  const repo = parseRepo(repoUrl);
  if (!repo) return null;
  return repo.parts.filter((part) => part !== "_git").join("/");
}

/** Round 6 (T2): one commit on its host, for a full 40-hex sha only; null otherwise. */
export function repoCommitUrl(repoUrl: string | null, commit: string | null): string | null {
  if (!repoUrl || !commit || !COMMIT_RE.test(commit)) return null;
  const repo = parseRepo(repoUrl);
  if (!repo) return null;
  const path = encodePath(repo.parts);
  switch (repo.host) {
    case "github":
      return repo.parts.length === 2 ? `https://github.com/${path}/commit/${commit}` : null;
    case "gitlab":
      return `https://gitlab.com/${path}/-/commit/${commit}`;
    case "bitbucket":
      return repo.parts.length === 2 ? `https://bitbucket.org/${path}/commits/${commit}` : null;
    case "azure":
      return repo.parts.length === 4 && repo.parts[2] === "_git"
        ? `https://dev.azure.com/${path}/commit/${commit}`
        : null;
  }
}

/** The git host's name for a link's accessible name ("…in GitHub, opens in a new tab"). */
export function repoHostName(repoUrl: string | null): string | null {
  if (!repoUrl) return null;
  const repo = parseRepo(repoUrl);
  if (!repo) return null;
  return { github: "GitHub", gitlab: "GitLab", azure: "Azure DevOps", bitbucket: "Bitbucket" }[
    repo.host
  ];
}

const HOST_DOMAINS: Record<Host, string> = {
  github: "github.com",
  gitlab: "gitlab.com",
  azure: "dev.azure.com",
  bitbucket: "bitbucket.org",
};

/**
 * Round 7 T-38: the repository's own name (`skydata-saas-dbt-test`), without its owner, `.git`
 * or a credential, as the Transformations facts strip names it; null for an unknown host.
 */
export function repoName(repoUrl: string | null): string | null {
  if (!repoUrl) return null;
  const repo = parseRepo(repoUrl);
  return repo ? (repo.parts.at(-1) ?? null) : null;
}

/**
 * Round 7 T-38: the repository's page on its host, rebuilt from the parsed parts so a
 * `user:token@` in the stored address never reaches a link; null for an unknown host.
 */
export function repoWebUrl(repoUrl: string | null): string | null {
  if (!repoUrl) return null;
  const repo = parseRepo(repoUrl);
  if (!repo) return null;
  return `https://${HOST_DOMAINS[repo.host]}/${encodePath(repo.parts)}`;
}

/**
 * Round 7 T-38 (owner: "shouldn't it say branch name"): the word a person reads beside the ref
 * the transform checks out. The binding takes a branch, a tag or a 40-hex commit
 * (`dbtRefRefusal`); a commit is told by its shape, and a tag cannot be told from a branch
 * without asking the host, so every other ref reads Branch ("Empty means main").
 */
export function gitRefTerm(ref: string): "Branch" | "Tag or commit" {
  return COMMIT_RE.test(ref) ? "Tag or commit" : "Branch";
}
